import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box, Typography, Grid, Button, Stack, Chip, Avatar, IconButton, Tooltip, Rating, CircularProgress, InputBase,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Snackbar, Alert, Switch, Checkbox,
} from '@mui/material';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import UpcomingRoundedIcon from '@mui/icons-material/UpcomingRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { useSearchParams } from 'react-router-dom';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK } from '../../theme';
import { initialsFromName as initials, avatarTone } from '../../utils/format';
import { solidBtn, tonalBtn, ghostBtn, successBtn, dangerBtn, field, Panel, EmptyState, LoadAlert } from '../uiKit';
import {
    emptyValues, missingRequired, buildCustomPayload, fileNameFromPath,
    uploadFieldFile, MAX_FILE_BYTES, fmtBytes,
} from '../../Api/interviewFields';
import http, { apiErrorMessage } from '../../Api/http';
import {
    GetInterviews, GetInterviewFormOptions, ScheduleInterview, RescheduleInterview,
    MarkInterviewConducted, MarkInterviewNoShow, ReviewInterview, CancelInterview,
} from '../../Api/Api';

const IV_MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
// An interview's date as YYYY-MM-DD, whether the API sends ISO or "27 Oct 2026".
const ivISO = (v) => {
    const t = String(v || '');
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
    const m = /(\d{1,2})\s+([A-Za-z]{3})\w*\s+(\d{4})/.exec(t);
    const i = m ? IV_MONTHS.indexOf(m[2].toLowerCase()) : -1;
    return i < 0 ? '' : `${m[3]}-${String(i + 1).padStart(2, '0')}-${m[1].padStart(2, '0')}`;
};

const MODES = ['Video', 'In-person', 'Phone'];
const ROUNDS = ['Screening', 'Technical Round 1', 'Technical Round 2', 'Managerial Round', 'HR Round', 'Final Round'];
const DURATIONS = [15, 30, 45, 60, 90];

const MODE_ICON = { Video: VideocamRoundedIcon, 'In-person': PlaceRoundedIcon, Phone: CallRoundedIcon };

const MONTHS = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
const isoFromDisplayDate = (s) => {
    const m = String(s || '').match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
    return m ? `${m[3]}-${MONTHS[m[2].toLowerCase()] || '01'}-${String(m[1]).padStart(2, '0')}` : '';
};
const time24FromDisplay = (s) => {
    const m = String(s || '').match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return '';
    let h = Number(m[1]) % 12;
    if (/pm/i.test(m[3])) h += 12;
    return `${String(h).padStart(2, '0')}:${m[2]}`;
};
const dmyFromIso = (s) => {
    const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : (s || '');
};

// The reverse, for showing a stored dd-MM-yyyy answer in a native date input.
const isoFromDmy = (s) => {
    const m = String(s || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : (s || '');
};

const OUTCOMES = [
    { key: 'Selected', label: 'Selected', desc: 'Move ahead — clear this round', icon: CheckCircleRoundedIcon, color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' },
    { key: 'Rejected', label: 'Rejected', desc: 'Not a fit — a reason is required', icon: CancelRoundedIcon, color: '#E11D48', bg: '#FEF2F2', border: '#FECACA' },
    { key: 'On Hold', label: 'On Hold', desc: 'Park them and decide later', icon: PauseCircleFilledRoundedIcon, color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
];

const EMPTY_SCHEDULE = { vacancyId: '', candidateId: '', round: 'Screening', date: '', time: '10:00', durationMins: 30, mode: 'Video', locationOrLink: '', panel: '' };
const EMPTY_REVIEW = { outcome: '', rating: 0, feedback: '', rejectReason: '' };

const normalizeInterview = (iv) => ({
    id: iv.id,
    vacancyId: iv.vacancyId,
    candidateId: iv.candidateId,
    candidateName: iv.candidateName ?? '',
    vacancyTitle: iv.role ?? '',
    round: iv.round ?? '',
    date: iv.date ?? '',
    time: iv.time ?? '',
    durationMins: Number(iv.durationMinutes) || 0,
    durationLabel: iv.durationLabel || `${Number(iv.durationMinutes) || 0} min`,
    mode: iv.mode ?? '',
    locationOrLink: iv.meetingDetail ?? '',
    panel: iv.panel ? String(iv.panel).split(',').map((p) => p.trim()).filter(Boolean) : [],
    // Already { label, value } pairs, so they render without cross-referencing
    // the definitions — including fields since deleted.
    customFields: Array.isArray(iv.customFields) ? iv.customFields : [],
    status: iv.status ?? '',
    outcome: iv.outcome ?? null,
    rating: Number(iv.rating) || 0,
    feedback: iv.feedback ?? null,
    reviewedBy: iv.reviewedBy ?? null,
    reviewedOn: iv.reviewedOn ?? null,
});

// A saved answer. File-type values are a storage path, so they get a file
// affordance and open in a new tab when the API gives us something linkable;
// otherwise the name is shown with the full path on hover.
function CustomFieldValue({ c }) {
    const isFile = c.fieldType === 'File';
    const href = isFile ? (c.url || c.fileUrl || '') : '';
    const text = c.fieldType === 'YesNo'
        ? (c.value === true || c.value === 'true' ? 'Yes' : 'No')
        : Array.isArray(c.value)
            ? c.value.join(', ')
            : isFile
                ? fileNameFromPath(c.value)
                : String(c.value ?? '');

    const body = (
        <Stack
            direction="row"
            spacing={0.6}
            onClick={href ? () => window.open(href, '_blank', 'noopener') : undefined}
            sx={{
                alignItems: 'center', px: 1, py: 0.4, borderRadius: '6px', maxWidth: '100%',
                bgcolor: href ? PRIMARY_LIGHT : '#FAFAFC',
                border: `1px solid ${href ? '#C2DCEE' : '#EEF0F6'}`,
                cursor: href ? 'pointer' : 'default',
                '&:hover': href ? { bgcolor: '#D0E5F3' } : {},
            }}
        >
            {isFile && <AttachFileRoundedIcon sx={{ fontSize: 13, color: href ? PRIMARY : '#B6BDC9', flexShrink: 0 }} />}
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.4, flexShrink: 0 }}>
                {c.label}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: href ? PRIMARY : '#475569', textDecoration: href ? 'underline' : 'none' }} noWrap>
                {text}
            </Typography>
        </Stack>
    );

    if (!isFile) return body;
    return (
        <Tooltip arrow title={href ? 'Open the uploaded file' : `Stored at ${c.value}`}>
            {body}
        </Tooltip>
    );
}

function InterviewRow({ iv, busy, onConduct, onReview, onReschedule, onCancel, onNoShow, tone }) {
    const ModeIcon = MODE_ICON[iv.mode] || VideocamRoundedIcon;
    const reviewed = Boolean(iv.outcome);
    const outcome = OUTCOMES.find((o) => o.key === iv.outcome);

    return (
        <Box sx={{
            border: `1px solid ${tone || '#EEF0F6'}`, borderRadius: '9px', p: 1.8, bgcolor: '#fff',
            display: 'flex', alignItems: 'center', gap: 1.8, flexWrap: 'wrap',
            transition: 'border-color .15s, box-shadow .15s',
            '&:hover': { borderColor: '#DDE3EC', boxShadow: '0 4px 14px -8px rgba(16,24,40,0.18)' },
        }}>
            <Box sx={{ minWidth: 80, textAlign: 'center', px: 1.2, py: 0.9, borderRadius: '8px', bgcolor: PRIMARY_LIGHT, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: PRIMARY_DARK, lineHeight: 1.2 }}>{iv.time || '—'}</Typography>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: PRIMARY }}>{iv.durationLabel}</Typography>
            </Box>

            <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 175 }}>
                <Avatar sx={{ width: 40, height: 40, ...avatarTone(iv.candidateName), fontSize: 13.5, fontWeight: 700 }}>{initials(iv.candidateName)}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }} noWrap>{iv.candidateName}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }} noWrap>{iv.vacancyTitle}</Typography>
                </Box>
            </Stack>

            <Box sx={{ minWidth: 155, flex: 1 }}>
                <Chip label={iv.round} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#E0F2FE', color: '#0369A1' }} />
                <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center', mt: 0.5 }}>
                    <ModeIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                    <Typography sx={{ fontSize: 11.5, color: '#64748B' }} noWrap>{iv.mode}{iv.locationOrLink ? ` · ${iv.locationOrLink}` : ''}</Typography>
                </Stack>
                {iv.panel.length > 0 && (
                    <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center', mt: 0.3 }}>
                        <GroupsRoundedIcon sx={{ fontSize: 14, color: '#CBD2DD' }} />
                        <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{iv.panel.join(', ')}</Typography>
                    </Stack>
                )}
                {iv.customFields.length > 0 && (
                    <Stack direction="row" sx={{ mt: 0.7, flexWrap: 'wrap', gap: 0.6 }}>
                        {iv.customFields.map((c) => <CustomFieldValue key={c.fieldKey} c={c} />)}
                    </Stack>
                )}
            </Box>

            {reviewed && outcome && (
                <Box sx={{ minWidth: 170 }}>
                    <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center' }}>
                        <outcome.icon sx={{ fontSize: 17, color: outcome.color }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: outcome.color }}>{outcome.label}</Typography>
                        {iv.rating > 0 && <Rating value={iv.rating} readOnly size="small" sx={{ fontSize: 14 }} />}
                    </Stack>
                    {iv.feedback && <Typography sx={{ fontSize: 11.5, color: '#64748B', mt: 0.2, fontStyle: 'italic' }} noWrap>“{iv.feedback}”</Typography>}
                </Box>
            )}

            <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
                {iv.status === 'Scheduled' && (
                    <>
                        <Button onClick={() => onConduct(iv)} disabled={busy} startIcon={<CheckCircleRoundedIcon sx={{ fontSize: 16 }} />} sx={{ ...solidBtn, height: 34, px: 1.6, fontSize: 12 }}>
                            Mark Conducted
                        </Button>
                        <Tooltip arrow title="Reschedule">
                            <span><IconButton size="small" disabled={busy} onClick={() => onReschedule(iv)} sx={{ width: 34, height: 34, color: '#64748B', border: '1px solid #E6EAF1', borderRadius: '7px', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}>
                                <EditCalendarRoundedIcon sx={{ fontSize: 17 }} />
                            </IconButton></span>
                        </Tooltip>
                        <Tooltip arrow title="Candidate didn't turn up">
                            <span><IconButton size="small" disabled={busy} onClick={() => onNoShow(iv)} sx={{ width: 34, height: 34, color: '#64748B', border: '1px solid #E6EAF1', borderRadius: '7px', '&:hover': { color: '#B45309', bgcolor: '#FFF7ED' } }}>
                                <EventBusyRoundedIcon sx={{ fontSize: 17 }} />
                            </IconButton></span>
                        </Tooltip>
                        <Tooltip arrow title="Cancel interview">
                            <span><IconButton size="small" disabled={busy} onClick={() => onCancel(iv)} sx={{ width: 34, height: 34, color: '#64748B', border: '1px solid #E6EAF1', borderRadius: '7px', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                <CancelRoundedIcon sx={{ fontSize: 17 }} />
                            </IconButton></span>
                        </Tooltip>
                    </>
                )}

                {iv.status === 'Conducted' && !reviewed && (
                    <Button onClick={() => onReview(iv)} disabled={busy} startIcon={<RateReviewRoundedIcon sx={{ fontSize: 16 }} />} sx={{ ...successBtn, height: 34, px: 1.8, fontSize: 12 }}>
                        Add Review
                    </Button>
                )}

                {reviewed && iv.reviewedBy && (
                    <Chip label={`Reviewed by ${iv.reviewedBy}`} size="small" sx={{ height: 24, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
                )}

                {iv.status === 'No Show' && <Chip label="No show" size="small" sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: '#FFF7ED', color: '#B45309' }} />}
                {iv.status === 'Cancelled' && <Chip label="Cancelled" size="small" sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />}
            </Stack>
        </Box>
    );
}

// A titled group inside the Interview Management panel.
function Section({ title, icon: Icon, chip, chipColor = PRIMARY, chipBg = PRIMARY_LIGHT, hint, children }) {
    return (
        <Box sx={{ borderTop: '1px solid #EEF0F6' }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 1.8, pt: 1.6, pb: 0.4, flexWrap: 'wrap' }}>
                {Icon && <Icon sx={{ fontSize: 18, color: chipColor }} />}
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{title}</Typography>
                {chip && <Chip label={chip} size="small" sx={{ bgcolor: chipBg, color: chipColor, fontWeight: 700, fontSize: 11 }} />}
                {hint && <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{hint}</Typography>}
            </Stack>
            {children}
        </Box>
    );
}

export default function InterviewsTab() {
    const [, setParams] = useSearchParams();
    const [data, setData] = useState({ cards: {}, today: [], awaiting: [], upcoming: [], completed: [] });
    const [options, setOptions] = useState({ vacancies: [], rounds: ROUNDS, modes: MODES, durations: DURATIONS, fields: [] });
    const [customValues, setCustomValues] = useState({});
    const [uploading, setUploading] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);

    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY_SCHEDULE);
    const [review, setReview] = useState(null);
    const [rv, setRv] = useState(EMPTY_REVIEW);
    const [tried, setTried] = useState(false);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetInterviews);
            if (body?.error) throw new Error(body.message || 'Could not load interviews.');
            const d = body?.data || {};
            setData({
                cards: d.cards || {},
                today: (Array.isArray(d.today) ? d.today : []).map(normalizeInterview),
                awaiting: (Array.isArray(d.awaitingReview) ? d.awaitingReview : []).map(normalizeInterview),
                upcoming: (Array.isArray(d.upcoming) ? d.upcoming : []).map(normalizeInterview),
                completed: (Array.isArray(d.completed) ? d.completed : []).map(normalizeInterview),
            });
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load interviews.'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadOptions = useCallback(async () => {
        try {
            const { data: body } = await http.get(GetInterviewFormOptions);
            if (body?.error) return;
            const d = body?.data || {};
            setOptions({
                vacancies: (Array.isArray(d.vacancies) ? d.vacancies : []).map((v) => ({
                    id: v.id,
                    label: v.label || v.roleTitle || 'Vacancy',
                    candidates: (Array.isArray(v.candidates) ? v.candidates : []).map((c) => ({ id: c.id, label: c.label || c.fullName || 'Candidate' })),
                })),
                rounds: Array.isArray(d.rounds) && d.rounds.length ? d.rounds : ROUNDS,
                fields: Array.isArray(d.fields) ? d.fields : [],
                modes: Array.isArray(d.modes) && d.modes.length ? d.modes : MODES,
                durations: Array.isArray(d.durations) && d.durations.length ? d.durations : DURATIONS,
            });
        } catch { }
    }, []);

    useEffect(() => { load(); loadOptions(); }, [load, loadOptions]);

    const candidatesForVacancy = useMemo(
        () => options.vacancies.find((v) => v.id === form.vacancyId)?.candidates || [],
        [options.vacancies, form.vacancyId],
    );

    // The definitions come back with the rest of the form options, so the
    // controls can never drift from the vacancies and rounds beside them.
    // Spec says render in displayOrder; sorting here rather than trusting the
    // response order keeps it right if the API ever returns them unsorted.
    const customFields = [...options.fields].sort(
        (a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0),
    );

    const pickFile = async (f, picked) => {
        if (picked.size > MAX_FILE_BYTES) {
            notify(`${picked.name} is over ${fmtBytes(MAX_FILE_BYTES)}.`, 'warning');
            return;
        }
        setUploading(f.fieldKey);
        try {
            // Staged now and referenced by path; the interview itself is only
            // linked to it when the form is submitted.
            const res = await uploadFieldFile(f.fieldKey, picked);
            setCustomValues((v) => ({
                ...v,
                [f.fieldKey]: { path: res?.path || '', url: res?.url || '', name: picked.name, size: picked.size },
            }));
        } catch (err) {
            notify(apiErrorMessage(err, `Could not upload ${picked.name}.`), 'error');
        } finally {
            setUploading(null);
        }
    };

    const openSchedule = () => {
        setForm({ ...EMPTY_SCHEDULE, date: new Date().toISOString().slice(0, 10) });
        setCustomValues(emptyValues(customFields));
        setTried(false);
        setDialog({ mode: 'schedule' });
    };
    const openReschedule = (iv) => {
        setForm({
            vacancyId: iv.vacancyId, candidateId: iv.candidateId, round: iv.round,
            date: isoFromDisplayDate(iv.date), time: time24FromDisplay(iv.time),
            durationMins: iv.durationMins, mode: iv.mode, locationOrLink: iv.locationOrLink,
            panel: iv.panel.join(', '),
        });
        setTried(false);
        setDialog({ mode: 'reschedule', iv });
    };
    const closeDialog = () => { if (!saving) setDialog(null); };
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const todayIso = new Date().toISOString().slice(0, 10);
    const dateInPast = Boolean(form.date) && form.date < todayIso;
    const scheduleValid = form.vacancyId && form.candidateId && form.date && form.time && !dateInPast;

    const submitSchedule = async () => {
        setTried(true);
        if (!scheduleValid) {
            notify(dateInPast ? 'The interview date can’t be in the past — pick today or a later date.'
                : 'Pick a vacancy, a candidate, a date and a time.', 'warning');
            return;
        }

        setSaving(true);
        try {
            let body;
            if (dialog.mode === 'reschedule') {
                ({ data: body } = await http.put(RescheduleInterview, {
                    id: dialog.iv.id, date: dmyFromIso(form.date), time: form.time,
                    durationMinutes: Number(form.durationMins), mode: form.mode, meetingDetail: form.locationOrLink,
                }));
            } else {
                const missing = missingRequired(customFields, customValues);
                if (missing.length) {
                    notify(`Fill in ${missing.join(', ')} before scheduling.`, 'warning');
                    setSaving(false);
                    return;
                }
                ({ data: body } = await http.post(ScheduleInterview, {
                    vacancyId: form.vacancyId, candidateId: form.candidateId, round: form.round,
                    date: dmyFromIso(form.date), time: form.time, durationMinutes: Number(form.durationMins),
                    mode: form.mode, meetingDetail: form.locationOrLink, panel: form.panel,
                    customFields: buildCustomPayload(customFields, customValues),
                }));
            }
            if (body?.error) throw new Error(body.message || 'Could not save the interview.');
            notify(body?.message || (dialog.mode === 'reschedule' ? 'Interview rescheduled.' : 'Interview scheduled.'));
            setDialog(null);
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the interview.'), 'error');
            // validOptions means this form's copy of a dropdown is out of date
            // — pull the definitions again so the next attempt offers the real
            // choices instead of failing the same way.
            if (Array.isArray(err?.response?.data?.validOptions)) loadOptions();
        } finally {
            setSaving(false);
        }
    };

    const runAction = async (request, okMsg, errMsg) => {
        if (saving) return;
        setSaving(true);
        try {
            const { data: body } = await request();
            if (body?.error) throw new Error(body.message || errMsg);
            notify(body?.message || okMsg);
            await load();
            return true;
        } catch (err) {
            notify(apiErrorMessage(err, errMsg), 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const conduct = (iv) => runAction(() => http.put(MarkInterviewConducted, null, { params: { id: iv.id } }), `${iv.candidateName}'s ${iv.round} marked as conducted.`, 'Could not update the interview.');
    const noShow = (iv) => runAction(() => http.put(MarkInterviewNoShow, null, { params: { id: iv.id } }), `${iv.candidateName} marked as a no-show.`, 'Could not update the interview.');
    const cancel = (iv) => runAction(() => http.put(CancelInterview, null, { params: { id: iv.id } }), `Interview with ${iv.candidateName} cancelled.`, 'Could not cancel the interview.');

    const openReview = (iv) => { setReview(iv); setRv(EMPTY_REVIEW); setTried(false); };

    const reviewValid = rv.outcome && (rv.outcome !== 'Rejected' || rv.rejectReason.trim());

    const submitReview = async () => {
        setTried(true);
        if (!reviewValid) {
            notify(!rv.outcome ? 'Pick an outcome for this interview.' : 'A rejection needs a reason — the candidate’s record keeps it.', 'warning');
            return;
        }
        const feedback = rv.outcome === 'Rejected'
            ? [rv.rejectReason.trim(), rv.feedback.trim()].filter(Boolean).join(' — ')
            : rv.feedback.trim();
        const ok = await runAction(
            () => http.put(ReviewInterview, { id: review.id, outcome: rv.outcome, rating: rv.rating, feedback }),
            rv.outcome === 'Selected' ? `${review.candidateName} cleared ${review.round}.`
                : rv.outcome === 'Rejected' ? `${review.candidateName} rejected — reason saved.`
                    : `${review.candidateName} put on hold.`,
            'Could not save the review.',
        );
        if (ok) setReview(null);
    };

    // Search, vacancy, date and view filters for the interview lists.
    const [q, setQ] = useState('');
    const [vacancyFilter, setVacancyFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('');
    const [view, setView] = useState('all');
    const fd = useMemo(() => {
        const s = q.trim().toLowerCase();
        const pass = (iv) => {
            if (vacancyFilter !== 'all' && String(iv.vacancyId) !== vacancyFilter) return false;
            if (dateFilter && ivISO(iv.date) !== dateFilter) return false;
            if (!s) return true;
            return [iv.candidateName, iv.vacancyTitle].some((x) => String(x || '').toLowerCase().includes(s));
        };
        return { today: data.today.filter(pass), awaiting: data.awaiting.filter(pass), upcoming: data.upcoming.filter(pass), completed: data.completed.filter(pass) };
    }, [data, q, vacancyFilter, dateFilter]);
    const vacancyChoices = useMemo(() => {
        const seen = new Map();
        [...data.today, ...data.awaiting, ...data.upcoming, ...data.completed].forEach((iv) => {
            if (iv.vacancyId != null && !seen.has(String(iv.vacancyId))) seen.set(String(iv.vacancyId), iv.vacancyTitle || 'Unknown role');
        });
        return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [data]);
    const pills = [
        { key: 'all', label: 'All interviews', n: fd.today.length + fd.awaiting.length + fd.upcoming.length + fd.completed.length },
        { key: 'upcoming', label: 'Upcoming', n: fd.upcoming.length },
        { key: 'awaiting', label: 'Awaiting review', n: fd.awaiting.length },
        { key: 'completed', label: 'Completed', n: fd.completed.length },
    ];
    const show = (k) => view === 'all' || view === k;

    const rowProps = { busy: saving, onConduct: conduct, onReview: openReview, onReschedule: openReschedule, onCancel: cancel, onNoShow: noShow };
    const selectedOutcome = OUTCOMES.find((o) => o.key === rv.outcome);

    if (loading) {
        return <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>;
    }
    if (loadError) {
        return (
            <Box sx={{ p: 2 }}>
                <LoadAlert sx={{ borderRadius: '9px' }}
                    action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                    {loadError}
                </LoadAlert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Panel
                title="Interview Management"
                icon={EventAvailableRoundedIcon}
                chip={`${pills[0].n} interview${pills[0].n === 1 ? '' : 's'}`}
                action={(
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Tooltip arrow title="Reload">
                            <span>
                                <IconButton onClick={() => { load(); loadOptions(); }} disabled={saving} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', bgcolor: '#fff', color: '#64748B', height: 38, width: 38, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                                    <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button startIcon={<TuneRoundedIcon />} onClick={() => setParams({ tab: 'interview-fields' }, { replace: true })} sx={{ ...tonalBtn, height: 38, px: 1.8, fontSize: 13 }}>Interview Fields</Button>
                        <Button startIcon={<AddRoundedIcon />} onClick={openSchedule} sx={{ ...solidBtn, height: 38, px: 1.8, fontSize: 13 }}>Schedule Interview</Button>
                    </Stack>
                )}
            >
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, flexWrap: 'wrap', p: 1.6, borderBottom: '1px solid #EEF0F6' }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '9px', px: 1.6, height: 42, flex: 1, minWidth: 220 }}>
                        <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
                        <InputBase placeholder="Search candidates…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                    </Stack>
                    <TextField
                        select size="small" value={vacancyFilter} onChange={(e) => setVacancyFilter(e.target.value)}
                        sx={{ ...field, width: 200, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 42, bgcolor: '#fff' } }}
                    >
                        <MenuItem value="all" sx={{ fontSize: 13 }}>All vacancies</MenuItem>
                        {vacancyChoices.map(([id, t]) => <MenuItem key={id} value={id} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                    </TextField>
                    <TextField
                        type="date" size="small" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
                        slotProps={{ htmlInput: { 'aria-label': 'Interview date' } }}
                        sx={{ ...field, width: 160, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 42, bgcolor: '#fff' } }}
                    />
                </Stack>
                <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', px: 1.6, py: 1.2 }}>
                    {pills.map((p) => (
                        <Box
                            key={p.key} component="button" type="button" onClick={() => setView(p.key)}
                            sx={{
                                border: view === p.key ? `1px solid ${PRIMARY}33` : '1px solid transparent', bgcolor: view === p.key ? PRIMARY_LIGHT : 'transparent',
                                color: view === p.key ? PRIMARY : '#475569', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                                px: 1.8, py: 0.9, borderRadius: '9px', cursor: 'pointer', '&:hover': { bgcolor: PRIMARY_LIGHT },
                            }}
                        >
                            {p.label} {p.n}
                        </Box>
                    ))}
                </Stack>
            {view === 'all' && fd.today.length > 0 && (
            <Section
                title="Interviews Today"
                icon={TodayRoundedIcon}
                chip={`${fd.today.length} scheduled`}
            >
                {fd.today.length === 0 ? (
                    <EmptyState icon={TodayRoundedIcon} title="No interviews today" hint="Schedule one, or check the upcoming list below." />
                ) : (
                    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                        {fd.today.map((iv) => <InterviewRow key={iv.id} iv={iv} {...rowProps} tone="#B3D3E8" />)}
                    </Box>
                )}
            </Section>
            )}

            {show('awaiting') && fd.awaiting.length > 0 && (
            <Section
                title="Awaiting Review"
                icon={RateReviewRoundedIcon}
                chip={`${fd.awaiting.length} to review`}
                chipColor="#B45309"
                chipBg="#FFF7ED"
                hint="Conducted, but no outcome recorded yet"
            >
                {fd.awaiting.length === 0 ? (
                    <EmptyState icon={CheckCircleRoundedIcon} title="Nothing waiting on you" hint="Every conducted interview has an outcome on file." />
                ) : (
                    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                        {fd.awaiting.map((iv) => <InterviewRow key={iv.id} iv={iv} {...rowProps} tone="#FDE68A" />)}
                    </Box>
                )}
            </Section>
            )}

            {show('upcoming') && fd.upcoming.length > 0 && (
            <Section
                title="Upcoming Interviews"
                icon={UpcomingRoundedIcon}
                chip={`${fd.upcoming.length} scheduled`}
                chipColor="#0369A1"
                chipBg="#E0F2FE"
            >
                {fd.upcoming.length === 0 ? (
                    <EmptyState icon={UpcomingRoundedIcon} title="Nothing on the calendar" hint="Schedule an interview and it will show up here." />
                ) : (
                    <Box sx={{ p: 1.5 }}>
                        {Object.entries(fd.upcoming.reduce((acc, iv) => {
                            (acc[iv.date] = acc[iv.date] || []).push(iv);
                            return acc;
                        }, {})).map(([date, list]) => (
                            <Box key={date} sx={{ mb: 1.8, '&:last-of-type': { mb: 0 } }}>
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                                    <ScheduleRoundedIcon sx={{ fontSize: 15, color: '#94A3B8' }} />
                                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>{date}</Typography>
                                    <Chip label={`${list.length} interview${list.length === 1 ? '' : 's'}`} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
                                    <Box sx={{ flex: 1, height: '1px', bgcolor: '#EEF0F6' }} />
                                </Stack>
                                <Stack spacing={1.2}>
                                    {list.map((iv) => <InterviewRow key={iv.id} iv={iv} {...rowProps} />)}
                                </Stack>
                            </Box>
                        ))}
                    </Box>
                )}
            </Section>
            )}

            {show('completed') && fd.completed.length > 0 && (
            <Section
                title="Completed Interviews"
                icon={EventAvailableRoundedIcon}
                chip={`${fd.completed.length} decided`}
                chipColor="#16A34A"
                chipBg="#DCFCE7"
                hint="Outcome and feedback on record"
            >
                {fd.completed.length === 0 ? (
                    <EmptyState icon={EventAvailableRoundedIcon} title="No decisions yet" hint="Reviewed interviews land here with their outcome and feedback." />
                ) : (
                    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                        {fd.completed.map((iv) => <InterviewRow key={iv.id} iv={iv} {...rowProps} />)}
                    </Box>
                )}
            </Section>
            )}
            {pills.find((x) => x.key === view).n === 0 && (
                <EmptyState icon={EventAvailableRoundedIcon} title="No interviews to show" hint="Schedule an interview and it will show up here." />
            )}
            </Panel>

            <Dialog open={Boolean(dialog)} onClose={closeDialog} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>
                        {dialog?.mode === 'reschedule' ? `Reschedule — ${dialog.iv.candidateName}` : 'Schedule an Interview'}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        {dialog?.mode === 'reschedule'
                            ? 'Move this round to a new date and time. The round and candidate stay the same.'
                            : 'Pick the role, then the candidate applying for it.'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Grid container spacing={1.8}>
                        {dialog?.mode === 'schedule' && (
                            <>
                                <Grid size={12}>
                                    <TextField
                                        select label="Vacancy" size="small" fullWidth value={form.vacancyId}
                                        onChange={(e) => setForm((f) => ({ ...f, vacancyId: e.target.value, candidateId: '' }))}
                                        error={tried && !form.vacancyId} sx={field}
                                        helperText={options.vacancies.length === 0 ? 'No open vacancies with candidates yet.' : ' '}
                                    >
                                        {options.vacancies.map((v) => (
                                            <MenuItem key={v.id} value={v.id} sx={{ fontSize: 13.5 }}>{v.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        select label="Candidate" size="small" fullWidth value={form.candidateId}
                                        onChange={set('candidateId')} error={tried && !form.candidateId} sx={field}
                                        disabled={!form.vacancyId}
                                        helperText={form.vacancyId && candidatesForVacancy.length === 0 ? 'No candidates on this vacancy yet — add one on the Candidates tab.' : ' '}
                                    >
                                        {candidatesForVacancy.map((c) => (
                                            <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13.5 }}>{c.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid size={12}>
                                    <TextField select label="Round" size="small" fullWidth value={form.round} onChange={set('round')} sx={field}>
                                        {options.rounds.map((r) => <MenuItem key={r} value={r} sx={{ fontSize: 13.5 }}>{r}</MenuItem>)}
                                    </TextField>
                                </Grid>
                            </>
                        )}

                        <Grid size={{ xs: 12, sm: 5 }}>
                            <TextField label="Date" type="date" size="small" fullWidth value={form.date} onChange={set('date')}
                                error={(tried && !form.date) || dateInPast}
                                helperText={dateInPast ? 'Date can’t be in the past' : undefined}
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: todayIso } }} sx={field} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Time" type="time" size="small" fullWidth value={form.time} onChange={set('time')}
                                error={tried && !form.time} slotProps={{ inputLabel: { shrink: true } }} sx={field} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <TextField select label="Duration" size="small" fullWidth value={form.durationMins} onChange={set('durationMins')} sx={field}>
                                {options.durations.map((d) => <MenuItem key={d} value={d} sx={{ fontSize: 13.5 }}>{d} min</MenuItem>)}
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField select label="Mode" size="small" fullWidth value={form.mode} onChange={set('mode')} sx={field}>
                                {options.modes.map((m) => <MenuItem key={m} value={m} sx={{ fontSize: 13.5 }}>{m}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8 }}>
                            <TextField
                                label={form.mode === 'Video' ? 'Meeting link' : form.mode === 'Phone' ? 'Phone number' : 'Location'}
                                size="small" fullWidth value={form.locationOrLink} onChange={set('locationOrLink')} sx={field}
                                placeholder={form.mode === 'Video' ? 'meet.google.com/…' : form.mode === 'Phone' ? '98400 00000' : 'Meeting Room 2'}
                            />
                        </Grid>

                        {dialog?.mode === 'schedule' && (
                            <Grid size={12}>
                                <TextField label="Interview panel (comma separated)" size="small" fullWidth value={form.panel}
                                    onChange={set('panel')} placeholder="e.g. Karthik R, Divya Prakash" sx={field} />
                            </Grid>
                        )}

                        {/* Whatever an admin defined under Recruitment > Interview
                            Fields, rendered by type. No code change per field. */}
                        {dialog?.mode === 'schedule' && customFields.length > 0 && (
                            <Grid size={12}>
                                <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', mt: 0.4, mb: 0.2 }}>
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap' }}>
                                        Additional details
                                    </Typography>
                                    <Box sx={{ flex: 1, height: '1px', bgcolor: '#EEF0F6' }} />
                                </Stack>
                            </Grid>
                        )}
                        {dialog?.mode === 'schedule' && customFields.map((f) => {
                            const label = `${f.label}${f.isRequired ? ' *' : ''}`;
                            const val = customValues[f.fieldKey];
                            const wide = f.fieldType === 'YesNo' || f.fieldType === 'File';
                            return (
                                <Grid size={{ xs: 12, sm: wide ? 12 : 6 }} key={f.fieldKey}>
                                    {f.fieldType === 'YesNo' ? (
                                        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.4, py: 1, borderRadius: '8px', border: '1px solid #EEF0F6', bgcolor: '#FAFAFC' }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{label}</Typography>
                                            <Switch
                                                checked={Boolean(val)}
                                                onChange={(e) => setCustomValues((v) => ({ ...v, [f.fieldKey]: e.target.checked }))}
                                                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY } }}
                                            />
                                        </Stack>
                                    ) : f.fieldType === 'Dropdown' ? (
                                        <TextField
                                            select label={label} size="small" fullWidth
                                            value={val || ''}
                                            onChange={(e) => setCustomValues((v) => ({ ...v, [f.fieldKey]: e.target.value }))}
                                            slotProps={{ inputLabel: { shrink: true } }} sx={field}
                                        >
                                            <MenuItem value=""><em>—</em></MenuItem>
                                            {f.options.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: 13.5 }}>{o}</MenuItem>)}
                                        </TextField>
                                    ) : f.fieldType === 'MultiSelect' ? (
                                        <TextField
                                            select label={label} size="small" fullWidth
                                            value={Array.isArray(val) ? val : []}
                                            onChange={(e) => setCustomValues((v) => ({
                                                ...v,
                                                [f.fieldKey]: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                                            }))}
                                            slotProps={{
                                                inputLabel: { shrink: true },
                                                select: {
                                                    multiple: true,
                                                    renderValue: (sel) => (
                                                        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                                            {sel.map((o) => (
                                                                <Chip key={o} label={o} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY }} />
                                                            ))}
                                                        </Stack>
                                                    ),
                                                },
                                            }}
                                            sx={field}
                                        >
                                            {f.options.map((o) => (
                                                <MenuItem key={o} value={o} sx={{ fontSize: 13.5 }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={(Array.isArray(val) ? val : []).includes(o)}
                                                        sx={{ mr: 0.8, p: 0.4, color: '#CBD5E1', '&.Mui-checked': { color: PRIMARY } }}
                                                    />
                                                    {o}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    ) : f.fieldType === 'File' ? (
                                        <Box>
                                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#6B7280', mb: 0.6 }}>{label}</Typography>
                                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                <Button
                                                    component="label"
                                                    disabled={uploading === f.fieldKey}
                                                    startIcon={uploading === f.fieldKey
                                                        ? <CircularProgress size={15} />
                                                        : <UploadFileRoundedIcon sx={{ fontSize: 17 }} />}
                                                    sx={{ ...ghostBtn, height: 40, px: 1.6, fontSize: 12.5, flexShrink: 0 }}
                                                >
                                                    {uploading === f.fieldKey ? 'Uploading…' : (val?.path ? 'Change' : 'Choose file')}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        onChange={(e) => {
                                                            const picked = e.target.files?.[0];
                                                            e.target.value = '';
                                                            if (picked) pickFile(f, picked);
                                                        }}
                                                    />
                                                </Button>
                                                {val?.path ? (
                                                    <Chip
                                                        label={val.size ? `${val.name} · ${fmtBytes(val.size)}` : fileNameFromPath(val.name || val.path)}
                                                        onDelete={() => setCustomValues((v) => ({ ...v, [f.fieldKey]: null }))}
                                                        onClick={val.url ? () => window.open(val.url, '_blank', 'noopener') : undefined}
                                                        sx={{ height: 28, fontSize: 11.5, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY, maxWidth: '100%', cursor: val.url ? 'pointer' : 'default' }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ fontSize: 12, color: '#B6BDC9' }} noWrap>
                                                        Up to {fmtBytes(MAX_FILE_BYTES)}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                    ) : (
                                        <TextField
                                            label={label} size="small" fullWidth
                                            type={f.fieldType === 'Number' ? 'number' : f.fieldType === 'Date' ? 'date' : 'text'}
                                            value={f.fieldType === 'Date' ? isoFromDmy(val) : (val || '')}
                                            onChange={(e) => setCustomValues((v) => ({
                                                ...v,
                                                // The API takes dd-MM-yyyy; the native picker speaks yyyy-MM-dd.
                                                [f.fieldKey]: f.fieldType === 'Date' ? dmyFromIso(e.target.value) : e.target.value,
                                            }))}
                                            slotProps={{ inputLabel: { shrink: true } }} sx={field}
                                        />
                                    )}
                                </Grid>
                            );
                        })}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={submitSchedule} disabled={saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                        {saving ? 'Saving…' : dialog?.mode === 'reschedule' ? 'Save New Slot' : 'Schedule'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(review)} onClose={() => { if (!saving) setReview(null); }} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Review Interview</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        {review?.candidateName} · {review?.round}{review?.date ? ` · ${review.date}` : ''}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#374151', mb: 0.8 }}>How did they do?</Typography>
                    <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', mb: 2.2 }}>
                        <Rating value={rv.rating} onChange={(_, v) => setRv((r) => ({ ...r, rating: v || 0 }))} size="large" sx={{ '& .MuiRating-iconFilled': { color: '#F59E0B' } }} />
                        <Typography sx={{ fontSize: 12.5, color: '#98A0AE' }}>{rv.rating ? `${rv.rating} of 5` : 'Not rated'}</Typography>
                    </Stack>

                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#374151', mb: 1 }}>Outcome</Typography>
                    <Grid container spacing={1.2} sx={{ mb: 2 }}>
                        {OUTCOMES.map((o) => {
                            const on = rv.outcome === o.key;
                            return (
                                <Grid size={{ xs: 12, sm: 4 }} key={o.key}>
                                    <Box
                                        onClick={() => setRv((r) => ({ ...r, outcome: o.key }))}
                                        sx={{
                                            cursor: 'pointer', p: 1.5, borderRadius: '9px', height: '100%',
                                            border: `1.5px solid ${on ? o.color : tried && !rv.outcome ? '#FECACA' : '#E5E7EB'}`,
                                            bgcolor: on ? o.bg : '#fff', transition: '.15s',
                                            '&:hover': { borderColor: o.border },
                                        }}
                                    >
                                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mb: 0.4 }}>
                                            <o.icon sx={{ fontSize: 18, color: on ? o.color : '#94A3B8' }} />
                                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: on ? o.color : '#374151' }}>{o.label}</Typography>
                                        </Stack>
                                        <Typography sx={{ fontSize: 11, color: '#6B7280', lineHeight: 1.4 }}>{o.desc}</Typography>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {rv.outcome === 'Rejected' && (
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                label="Reason for rejection" size="small" fullWidth multiline minRows={2} autoFocus
                                placeholder="e.g. Strong on React, but no real experience with state management at scale."
                                value={rv.rejectReason}
                                onChange={(e) => setRv((r) => ({ ...r, rejectReason: e.target.value }))}
                                error={tried && !rv.rejectReason.trim()}
                                helperText={tried && !rv.rejectReason.trim()
                                    ? 'Required — this is what stays on the candidate’s record.'
                                    : 'Saved against the candidate, so the decision is never unexplained later.'}
                                sx={field}
                            />
                        </Box>
                    )}

                    <TextField
                        label="Feedback (optional)" size="small" fullWidth multiline minRows={3}
                        placeholder="What went well, what didn't, anything the next round should probe."
                        value={rv.feedback}
                        onChange={(e) => setRv((r) => ({ ...r, feedback: e.target.value }))}
                        sx={field}
                    />

                    {rv.outcome && (
                        <Box sx={{ mt: 2, p: 1.6, borderRadius: '9px', bgcolor: selectedOutcome.bg, border: `1px solid ${selectedOutcome.border}` }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: selectedOutcome.color, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.4 }}>What happens next</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#334155' }}>
                                {rv.outcome === 'Selected' && <><strong>{review?.candidateName}</strong> clears this round and is marked <strong>Selected</strong>. Schedule the next round, or mark them joined from the Candidates tab once they accept.</>}
                                {rv.outcome === 'Rejected' && <><strong>{review?.candidateName}</strong> is marked <strong>Rejected</strong> and drops out of the pipeline. The reason is kept on their record.</>}
                                {rv.outcome === 'On Hold' && <><strong>{review?.candidateName}</strong> is parked as <strong>On Hold</strong> — they stay in the pipeline and can be picked up again later.</>}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setReview(null)} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button
                        onClick={submitReview} disabled={saving}
                        startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                        sx={rv.outcome === 'Rejected'
                            ? { ...dangerBtn, height: 40, px: 2.4, bgcolor: '#DC2626', color: '#fff', border: 'none', '&:hover': { bgcolor: '#B91C1C' } }
                            : { ...successBtn, height: 40, px: 2.4 }}
                    >
                        {saving ? 'Saving…' : 'Save Review'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={Boolean(snack)} autoHideDuration={3800} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
